import React, { useState, useContext, useRef, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom'; // Import Link
import { Form, Button, Alert, Container, Row, Col, Image } from 'react-bootstrap';
import { Fade } from 'react-awesome-reveal'; // Import Fade for animation
import rightIcon from '../../assets/rightIcon.svg';
import logo from '../../assets/app-logo.png';
import { friendlyAuthError } from './authErrors';

// Import the specific CSS for this component
import './Register.css';

// Password policy — the single source of truth for the placeholder, the rule
// list and the submit-time validation. These three used to disagree: the
// placeholder said 6, the rules said 12 and the code enforced 6 (NVDA end-user
// test, 27 Jul 2026).
const PASSWORD_MIN = 12;
const COMPLEXITY = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/;

const Register = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const { register } = useContext(AuthContext);
    const navigate = useNavigate();

    const passwordRef = useRef(null);
    const confirmRef = useRef(null);
    const errorRef = useRef(null);

    // Move focus to the submit error so it is announced — the alert alone
    // leaves keyboard focus on the Sign Up button.
    useEffect(() => {
        if (error) errorRef.current?.focus();
    }, [error]);

    // Live rule state — drives both the visible ticks and the text each rule
    // exposes to a screen reader.
    const rules = [
        {
            id: 'rule-length',
            label: `Must be at least ${PASSWORD_MIN} characters long`,
            met: password.length >= PASSWORD_MIN,
        },
        {
            id: 'rule-complexity',
            label: 'Should include uppercase, lowercase, numbers, and special characters',
            met: COMPLEXITY.test(password),
        },
        {
            id: 'rule-match',
            label: 'Must match the confirm password field',
            met: password.length > 0 && password === confirmPassword,
        },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const errors = {};
        if (password.length < PASSWORD_MIN) {
            errors.password = `Password must be at least ${PASSWORD_MIN} characters long.`;
        } else if (!COMPLEXITY.test(password)) {
            errors.password = 'Password must include an uppercase letter, a lowercase letter, a number and a special character.';
        }
        if (password !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match. Please re-enter them.';
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            // Move focus to the first field at fault so the error is announced
            // and the user lands where the correction is made.
            if (errors.password) passwordRef.current?.focus();
            else confirmRef.current?.focus();
            return;
        }

        setFieldErrors({});
        try {
            await register(name, email, password);
            navigate('/logout'); // Navigate to dashboard or login after successful registration
        } catch (err) {
            setError(friendlyAuthError(err, 'Registration failed. Please try again.'));
        }
    };

    return (
        // Main landmark with padding for fixed navbar and background (docx round-2)
        <main id="main-content" className="register-page-container">
            <Container>
                <Row className="justify-content-center">
                    {/* Adjust column width for different screen sizes */}
                    <Col xs={12} sm={10} md={8} lg={6}>
                        {/* Link (not onClick img) so the logo is keyboard-operable (docx round-2) */}
                        <Link to="/" className="d-inline-block">
                            <Image src={logo} alt="Right Spade Home" className='mb-3 ms-4 ms-md-0 cursor-pointer' />
                        </Link>
                        <Fade direction="up" triggerOnce cascade damping={0.1}>
                            {/* Wrapper for the form card styling */}
                            <div className="register-form-wrapper">
                                {/* h1: page heading was missing entirely (docx round-2) */}
                                <h1 className="register-title">Create Account</h1>
                                {error && (
                                    <Alert variant="danger" ref={errorRef} tabIndex={-1}>{error}</Alert>
                                )}
                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3" controlId="formBasicName">
                                        <Form.Label>Your Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter your name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicEmail">
                                        <Form.Label>E-mail Address</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder="Enter your email address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicPassword">
                                        <Form.Label>Set Password</Form.Label>
                                        <Form.Control
                                            ref={passwordRef}
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder={`Password (min. ${PASSWORD_MIN} characters)`}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            aria-invalid={fieldErrors.password ? 'true' : undefined}
                                            aria-describedby={`passwordHelpBlock${fieldErrors.password ? ' passwordError' : ''}`}
                                        />
                                        {fieldErrors.password && (
                                            <p id="passwordError" className="field-error">{fieldErrors.password}</p>
                                        )}
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="formBasicPassword2">
                                        <Form.Label>Confirm Password</Form.Label>
                                        <Form.Control
                                            ref={confirmRef}
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Enter password to confirm"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            aria-invalid={fieldErrors.confirmPassword ? 'true' : undefined}
                                            aria-describedby={fieldErrors.confirmPassword ? 'confirmPasswordError' : undefined}
                                        />
                                        {fieldErrors.confirmPassword && (
                                            <p id="confirmPasswordError" className="field-error">{fieldErrors.confirmPassword}</p>
                                        )}
                                    </Form.Group>

                                    <Form.Check
                                        type="checkbox"
                                        id="showPassword"
                                        className="mb-3 show-password-check"
                                        label="Show password"
                                        checked={showPassword}
                                        onChange={(e) => setShowPassword(e.target.checked)}
                                    />

                                    {/* Password requirements. aria-live announces a rule only when it
                                        flips between met and not met, so typing is not chatty. The
                                        met/not-met word carries the state for screen readers — the
                                        tick colour alone would fail WCAG 1.4.1. */}
                                    <div>
                                        <Row
                                            id="passwordHelpBlock"
                                            as="ul"
                                            className='error-wrp password-rules'
                                            aria-live="polite"
                                        >
                                            {rules.map((rule) => (
                                                <Col
                                                    as="li"
                                                    sm={12}
                                                    key={rule.id}
                                                    className={`error-text ${rule.met ? 'rule-met' : 'rule-unmet'}`}
                                                >
                                                    <img src={rightIcon} alt="" aria-hidden="true" />
                                                    <p className='error-msg'>
                                                        {rule.label}
                                                        <span className="visually-hidden">
                                                            {rule.met ? ' — met' : ' — not met'}
                                                        </span>
                                                    </p>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                    <Button type="submit" className="register-button">
                                        Sign Up
                                    </Button>
                                </Form>

                                {/* Link to Login Page */}
                                <p className="login-link-text mt-3">
                                    <Link to="/login">Have an account?</Link>
                                </p>
                            </div>
                        </Fade>
                    </Col>
                </Row>
            </Container>
        </main>
    );
};

export default Register;